import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import useDebounce from '../../hooks/useDebounce';
import { ordersAPI, leadsAPI, settingsAPI } from '../../services/api';
import { useTranslation } from 'react-i18next';
import AdminSidebar from '../../components/AdminSidebar';
import LanguageToggle from '../../components/LanguageToggle';
import {
  Search, Filter, Plus, Eye, Edit, Package,
  Clock, CheckCircle, Truck, X, AlertCircle, Save,
  Trash2, User, Mail, Phone, Building, DollarSign,
  Calendar, FileText, Settings, Sparkles, Brain
} from 'lucide-react';
import '../Admin/AdminDashboard.css';
import './AdminOrders.css';

const AdminOrders = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [orders, setOrders] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [aiSuggestion, setAiSuggestion] = useState(null);
  const [loadingAI, setLoadingAI] = useState(false);
  const [taxRate, setTaxRate] = useState(0.22);
  const [notification, setNotification] = useState({ show: false, type: '', message: '', details: '' });

  // Filters derived from URL params
  const filters = {
    search: searchParams.get('search') || '',
    status: searchParams.get('status') || '',
    priority: searchParams.get('priority') || '',
  };

  // Update a single filter key in the URL
  const updateFilter = (key, value) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
    setSearchParams(newParams, { replace: true });
  };

  // Local search state — debounced before writing to URL
  const [localSearch, setLocalSearch] = useState(searchParams.get('search') || '');
  const debouncedSearch = useDebounce(localSearch, 300);

  useEffect(() => {
    const newParams = new URLSearchParams(searchParams);
    if (debouncedSearch) {
      newParams.set('search', debouncedSearch);
    } else {
      newParams.delete('search');
    }
    setSearchParams(newParams, { replace: true });
  }, [debouncedSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  const [formData, setFormData] = useState({
    leadId: '',
    customer: { name: '', email: '', phone: '', company: '' },
    items: [{ description: '', quantity: 1, material: '', finish: '', unitPrice: 0 }],
    pricing: { subtotal: 0, shipping: 0, tax: 0, discount: 0, total: 0 },
    shipping: { method: '', address: { street: '', city: '', province: '', postalCode: '', country: 'Italia' } },
    priority: 'normal'
  });

  useEffect(() => {
    loadOrders();
  }, [debouncedSearch, filters.status, filters.priority]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadLeads();
  }, []);

  useEffect(() => {
    const loadTaxRate = async () => {
      try {
        const response = await settingsAPI.getAll({ category: 'pricing' });
        const rate = response.data.settings?.[0]?.settings?.taxRate;
        if (rate !== undefined) {
          setTaxRate(rate / 100);
        }
      } catch (error) {
        console.error('Error loading tax rate:', error);
      }
    };
    loadTaxRate();
  }, []);

  // Auto-hide notification
  useEffect(() => {
    if (notification.show) {
      const timer = setTimeout(() => {
        setNotification({ show: false, type: '', message: '', details: '' });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [notification.show]);

  // Escape key — close Order View modal
  useEffect(() => {
    if (!selectedOrder) return;
    const handler = (e) => { if (e.key === 'Escape') setSelectedOrder(null); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [selectedOrder]);

  // Escape key — close Create Order modal
  useEffect(() => {
    if (!showCreateModal) return;
    const handler = (e) => { if (e.key === 'Escape') { setShowCreateModal(false); resetForm(); } };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [showCreateModal]);

  // Escape key — close Edit Order modal
  useEffect(() => {
    if (!showEditModal) return;
    const handler = (e) => { if (e.key === 'Escape') setShowEditModal(false); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [showEditModal]);

  const showNotification = (type, message, details = '') => {
    setNotification({ show: true, type, message, details });
  };

  const loadOrders = async () => {
    try {
      setLoading(true);
      const res = await ordersAPI.getAll({ ...filters, search: debouncedSearch });
      setOrders(res.data.orders);
    } catch (error) {
      showNotification('error', t('admin.orders.errorLoad'), error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadLeads = async () => {
    try {
      const res = await leadsAPI.getAll({ status: 'new,contacted,quoted' });
      setLeads(res.data.leads || []);
    } catch (error) {
      console.error('Error loading leads:', error);
    }
  };

  const handleLeadSelect = (leadId) => {
    const lead = leads.find(l => l._id === leadId);
    if (lead) {
      setFormData({
        ...formData,
        leadId,
        customer: {
          name: lead.name,
          email: lead.email,
          phone: lead.phone || '',
          company: lead.company || ''
        },
        priority: lead.urgency || 'normal'
      });
    }
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = value;
    
    // Auto-calculate total price
    if (field === 'quantity' || field === 'unitPrice') {
      newItems[index].totalPrice = newItems[index].quantity * newItems[index].unitPrice;
    }
    
    setFormData({ ...formData, items: newItems });
    calculatePricing(newItems, formData.pricing.shipping, formData.pricing.discount);
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { description: '', quantity: 1, material: '', finish: '', unitPrice: 0 }]
    });
  };

  const removeItem = (index) => {
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: newItems });
    calculatePricing(newItems, formData.pricing.shipping, formData.pricing.discount);
  };

  const calculatePricing = (items, shipping = 0, discount = 0) => {
    const subtotal = items.reduce((sum, item) => sum + (item.totalPrice || 0), 0);
    const tax = subtotal * taxRate; // IVA loaded from settings
    const total = subtotal + shipping + tax - discount;
    
    setFormData(prev => ({
      ...prev,
      pricing: { ...prev.pricing, subtotal, tax, total }
    }));
  };

  const handleCreateOrder = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.customer.name || !formData.customer.email) {
      showNotification('error', t('admin.orders.errorRequired'), t('admin.orders.errorRequiredDetail'));
      return;
    }
    
    if (formData.items.length === 0 || !formData.items[0].description) {
      showNotification('error', t('admin.orders.errorNoItems'), t('admin.orders.errorNoItemsDetail'));
      return;
    }
    
    try {
      const res = await ordersAPI.create(formData);
      showNotification('success', t('admin.orders.successCreate'), `Ordine ${res.data.order.orderNumber}`);
      setShowCreateModal(false);
      resetForm();
      loadOrders();
    } catch (error) {
      showNotification('error', t('admin.orders.errorLoad'), error.response?.data?.message || error.message);
    }
  };

  const handleUpdateOrder = async (e) => {
    e.preventDefault();
    
    try {
      const res = await ordersAPI.update(editingOrder._id, editingOrder);
      showNotification('success', t('admin.orders.successUpdate'), `Ordine ${res.data.order.orderNumber}`);
      setShowEditModal(false);
      setEditingOrder(null);
      loadOrders();
    } catch (error) {
      showNotification('error', t('admin.orders.errorLoad'), error.response?.data?.message || error.message);
    }
  };

  const handleDeleteOrder = async (orderId) => {
    if (!window.confirm(t('admin.orders.confirmCancel'))) return;
    
    try {
      await ordersAPI.update(orderId, { status: 'cancelled' });
      showNotification('success', t('admin.orders.successCancel'), t('admin.orders.successCancelDetail'));
      loadOrders();
    } catch (error) {
      showNotification('error', t('admin.orders.errorLoad'), error.response?.data?.message || error.message);
    }
  };

  const openEditModal = (order) => {
    setEditingOrder({ ...order });
    setShowEditModal(true);
  };

  const resetForm = () => {
    setFormData({
      leadId: '',
      customer: { name: '', email: '', phone: '', company: '' },
      items: [{ description: '', quantity: 1, material: '', finish: '', unitPrice: 0 }],
      pricing: { subtotal: 0, shipping: 0, tax: 0, discount: 0, total: 0 },
      shipping: { method: '', address: { street: '', city: '', province: '', postalCode: '', country: 'Italia' } },
      priority: 'normal'
    });
    setAiSuggestion(null);
  };

  const getAIQuoteSuggestion = async () => {
    if (!formData.items[0].description) {
      showNotification('warning', t('admin.orders.aiNeedItems'));
      return;
    }

    setLoadingAI(true);
    try {
      const res = await ordersAPI.getAIQuoteSuggestion(formData);
      if (res.data.success) {
        setAiSuggestion(res.data.suggestion);
        showNotification('success', t('admin.orders.aiSuccess'), t('admin.orders.aiSuccessDetail'));
      } else {
        showNotification('info', t('admin.orders.aiUnavailable'), t('admin.orders.aiUnavailableDetail'));
        setAiSuggestion(res.data.suggestion);
      }
    } catch (error) {
      showNotification('error', t('admin.orders.aiError'), error.message);
    } finally {
      setLoadingAI(false);
    }
  };

  const applyAISuggestion = () => {
    if (!aiSuggestion?.items?.length) return;
    const updatedItems = formData.items.map((item, index) => {
      const aiItem = aiSuggestion.items[index];
      return aiItem ? { ...item, unitPrice: aiItem.suggestedUnitPrice || item.unitPrice } : item;
    });
    setFormData({ ...formData, items: updatedItems });
    calculatePricing(updatedItems, formData.pricing.shipping, formData.pricing.discount);
    setAiSuggestion(null);
    showNotification('success', t('admin.orders.aiApplied'));
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: '#6b7280',
      quoted: '#f59e0b',
      approved: '#3b82f6',
      in_production: '#8b5cf6',
      quality_check: '#ec4899',
      shipped: '#10b981',
      delivered: '#14b8a6',
      cancelled: '#ef4444'
    };
    return colors[status] || '#6b7280';
  };

  const getStatusLabel = (status) => {
    return t(`admin.orders.status.${status}`, status);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount || 0);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  return (
    <div className="admin-layout">
      <AdminSidebar />
      
      <div className="admin-content">
        {/* Notification Toast */}
        {notification.show && (
          <div className={`notification-toast notification-${notification.type}`}>
            <div className="notification-content">
              <strong>{notification.message}</strong>
              {notification.details && <p>{notification.details}</p>}
            </div>
            <button onClick={() => setNotification({ show: false, type: '', message: '', details: '' })} aria-label={t('admin.orders.closeNotification')}>
              <X size={18} />
            </button>
          </div>
        )}

        <header className="content-header">
          <div>
            <h1>{t('admin.orders.title')}</h1>
            <p>{t('admin.orders.subtitle')}</p>
          </div>
          <div style={{ display: 'flex', gap: '1rem' }}>
            <LanguageToggle />
            <button 
              className="admin-btn admin-btn-primary"
              onClick={() => setShowCreateModal(true)}
            >
              <Plus size={18} />
              {t('admin.orders.new')}
            </button>
          </div>
        </header>

        {/* Filters */}
        <div className="admin-filters">
          <div className="admin-search">
            <Search size={20} />
            <input
              type="text"
              placeholder={t('admin.orders.search')}
              value={localSearch}
              onChange={(e) => setLocalSearch(e.target.value)}
            />
          </div>

          <select
            value={filters.status}
            onChange={(e) => updateFilter('status', e.target.value)}
            className="admin-select"
          >
            <option value="">{t('admin.orders.allStatuses')}</option>
            <option value="pending">{t('admin.orders.status.pending')}</option>
            <option value="quoted">{t('admin.orders.status.quoted')}</option>
            <option value="approved">{t('admin.orders.status.approved')}</option>
            <option value="in_production">{t('admin.orders.status.in_production')}</option>
            <option value="quality_check">{t('admin.orders.status.quality_check')}</option>
            <option value="shipped">{t('admin.orders.status.shipped')}</option>
            <option value="delivered">{t('admin.orders.status.delivered')}</option>
            <option value="cancelled">{t('admin.orders.status.cancelled')}</option>
          </select>

          <select
            value={filters.priority}
            onChange={(e) => updateFilter('priority', e.target.value)}
            className="admin-select"
          >
            <option value="">{t('admin.orders.allPriorities')}</option>
            <option value="urgent">{t('admin.orders.priority.urgent')}</option>
            <option value="high">{t('admin.orders.priority.high')}</option>
            <option value="normal">{t('admin.orders.priority.normal')}</option>
            <option value="low">{t('admin.orders.priority.low')}</option>
          </select>

          {(filters.status || filters.priority || filters.search) && (
            <button
              onClick={() => {
                setLocalSearch('');
                setSearchParams(new URLSearchParams(), { replace: true });
              }}
              className="admin-btn admin-btn-secondary"
            >
              <X size={18} />
              {t('admin.orders.reset')}
            </button>
          )}
        </div>

        {/* Orders Grid */}
        <div className="orders-grid">
          {orders.map((order) => (
            <div key={order._id} className="order-card">
              <div className="order-header">
                <div>
                  <h3>{order.orderNumber}</h3>
                  <p className="order-customer">{order.customer.name}</p>
                </div>
                <span
                  className="order-status"
                  style={{ background: `${getStatusColor(order.status)}20`, color: getStatusColor(order.status) }}
                >
                  {getStatusLabel(order.status)}
                </span>
              </div>

              <div className="order-details">
                <div className="order-detail-item">
                  <Package size={16} />
                  <span>{t('admin.orders.itemsCount', { count: order.items?.length || 0 })}</span>
                </div>
                <div className="order-detail-item">
                  <Clock size={16} />
                  <span>{formatDate(order.createdAt)}</span>
                </div>
              </div>

              <div className="order-pricing">
                <span>{t('admin.orders.total')}</span>
                <strong>{formatCurrency(order.pricing?.total)}</strong>
              </div>

              <div className="order-actions">
                <button
                  onClick={() => setSelectedOrder(order)}
                  className="action-btn action-btn-view"
                >
                  <Eye size={16} />
                  {t('admin.orders.viewLabel')}
                </button>
                <button 
                  onClick={() => openEditModal(order)}
                  className="action-btn action-btn-edit"
                >
                  <Edit size={16} />
                  {t('admin.orders.editLabel')}
                </button>
              </div>

              {order.priority === 'urgent' && (
                <div className="order-urgent-badge">{t('admin.orders.urgentBadge')}</div>
              )}
            </div>
          ))}
        </div>

        {orders.length === 0 && !loading && (
          <div className="admin-empty">
            <AlertCircle size={48} />
            <p>{t('admin.orders.empty')}</p>
          </div>
        )}

        {/* Order Detail Modal */}
        {selectedOrder && (
          <div className="admin-modal-overlay" onClick={() => setSelectedOrder(null)}>
            <div className="admin-modal order-modal" role="dialog" aria-modal="true" aria-labelledby="order-view-modal-title" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <div>
                  <h2 id="order-view-modal-title">{selectedOrder.orderNumber}</h2>
                  <p style={{ color: '#a1a1aa', margin: '4px 0 0 0' }}>
                    {selectedOrder.customer.name}
                  </p>
                </div>
                <button onClick={() => setSelectedOrder(null)} className="modal-close" aria-label={t('admin.common.close')}>
                  <X size={24} />
                </button>
              </div>

              <div className="modal-body">
                <div className="order-full-details">
                  <section>
                    <h3>{t('admin.orders.customerInfo')}</h3>
                    <div className="info-grid">
                      <div><strong>{t('admin.leads.fieldName')}</strong> {selectedOrder.customer.name}</div>
                      <div><strong>{t('admin.leads.fieldEmail')}</strong> {selectedOrder.customer.email}</div>
                      <div><strong>{t('admin.leads.fieldPhone')}</strong> {selectedOrder.customer.phone || t('admin.common.na')}</div>
                    </div>
                  </section>

                  <section>
                    <h3>{t('admin.orders.orderedItems')}</h3>
                    {selectedOrder.items?.map((item, index) => (
                      <div key={index} className="order-item">
                        <div>
                          <strong>{item.description}</strong>
                          <p>{t('admin.orders.itemDetail', { material: item.material, quantity: item.quantity })}</p>
                        </div>
                        <span>{formatCurrency(item.totalPrice)}</span>
                      </div>
                    ))}
                  </section>

                  <section>
                    <h3>{t('admin.orders.pricingSummary')}</h3>
                    <div className="pricing-summary">
                      <div><span>{t('admin.orders.subtotal')}:</span> <span>{formatCurrency(selectedOrder.pricing?.subtotal)}</span></div>
                      <div><span>{t('admin.orders.shippingLabel')}:</span> <span>{formatCurrency(selectedOrder.pricing?.shipping)}</span></div>
                      <div><span>{t('admin.orders.taxLabel')}:</span> <span>{formatCurrency(selectedOrder.pricing?.tax)}</span></div>
                      <div className="pricing-total">
                        <strong>{t('admin.orders.total')}:</strong>
                        <strong>{formatCurrency(selectedOrder.pricing?.total)}</strong>
                      </div>
                    </div>
                  </section>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Create Order Modal */}
        {showCreateModal && (
          <div className="admin-modal-overlay" onClick={() => { setShowCreateModal(false); resetForm(); }}>
            <div className="admin-modal order-create-modal" role="dialog" aria-modal="true" aria-labelledby="order-create-modal-title" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2 id="order-create-modal-title">{t('admin.orders.newOrderTitle')}</h2>
                <button onClick={() => { setShowCreateModal(false); resetForm(); }} className="modal-close" aria-label={t('admin.common.close')}>
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={handleCreateOrder}>
                <div className="modal-body">
                  {/* Lead Selection */}
                  <section className="form-section">
                    <h3><User size={18} /> {t('admin.orders.linkLead')}</h3>
                    <select
                      value={formData.leadId}
                      onChange={(e) => handleLeadSelect(e.target.value)}
                      className="admin-input"
                      autoFocus
                    >
                      <option value="">{t('admin.orders.noLead')}</option>
                      {leads.map(lead => (
                        <option key={lead._id} value={lead._id}>
                          {lead.name} - {lead.email}
                        </option>
                      ))}
                    </select>
                  </section>

                  {/* Customer Information */}
                  <section className="form-section">
                    <h3><User size={18} /> {t('admin.orders.customerInfo')}</h3>
                    <div className="form-grid">
                      <div className="form-group">
                        <label>{t('admin.orders.labelName')}</label>
                        <input
                          type="text"
                          value={formData.customer.name}
                          onChange={(e) => setFormData({
                            ...formData,
                            customer: { ...formData.customer, name: e.target.value }
                          })}
                          className="admin-input"
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label>{t('admin.orders.labelEmail')}</label>
                        <input
                          type="email"
                          value={formData.customer.email}
                          onChange={(e) => setFormData({
                            ...formData,
                            customer: { ...formData.customer, email: e.target.value }
                          })}
                          className="admin-input"
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label>{t('admin.orders.labelPhone')}</label>
                        <input
                          type="tel"
                          value={formData.customer.phone}
                          onChange={(e) => setFormData({
                            ...formData,
                            customer: { ...formData.customer, phone: e.target.value }
                          })}
                          className="admin-input"
                        />
                      </div>
                      <div className="form-group">
                        <label>{t('admin.orders.labelCompany')}</label>
                        <input
                          type="text"
                          value={formData.customer.company}
                          onChange={(e) => setFormData({
                            ...formData,
                            customer: { ...formData.customer, company: e.target.value }
                          })}
                          className="admin-input"
                        />
                      </div>
                    </div>
                  </section>

                  {/* Items */}
                  <section className="form-section">
                    <h3><Package size={18} /> Articoli</h3>
                    {formData.items.map((item, index) => (
                      <div key={index} className="item-row">
                        <div className="form-grid">
                          <div className="form-group">
                            <label>{t('admin.orders.labelDescription')}</label>
                            <input
                              type="text"
                              value={item.description}
                              onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                              className="admin-input"
                              required
                            />
                          </div>
                          <div className="form-group">
                            <label>{t('admin.orders.labelMaterial')}</label>
                            <select
                              value={item.material}
                              onChange={(e) => handleItemChange(index, 'material', e.target.value)}
                              className="admin-input"
                            >
                              <option value="">{t('admin.orders.selectOption')}</option>
                              <option value="PLA">PLA</option>
                              <option value="ABS">ABS</option>
                              <option value="PETG">PETG</option>
                              <option value="Nylon">Nylon</option>
                              <option value="TPU">TPU</option>
                              <option value="ASA">ASA</option>
                            </select>
                          </div>
                          <div className="form-group">
                            <label>{t('admin.orders.labelFinish')}</label>
                            <input
                              type="text"
                              value={item.finish}
                              onChange={(e) => handleItemChange(index, 'finish', e.target.value)}
                              className="admin-input"
                              placeholder={t('admin.orders.finishPlaceholder')}
                            />
                          </div>
                          <div className="form-group">
                            <label>{t('admin.orders.labelQuantity')}</label>
                            <input
                              type="number"
                              min="1"
                              value={item.quantity}
                              onChange={(e) => handleItemChange(index, 'quantity', parseInt(e.target.value) || 1)}
                              className="admin-input"
                              required
                            />
                          </div>
                          <div className="form-group">
                            <label>{t('admin.orders.labelUnitPrice')}</label>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={item.unitPrice}
                              onChange={(e) => handleItemChange(index, 'unitPrice', parseFloat(e.target.value) || 0)}
                              className="admin-input"
                              required
                            />
                          </div>
                          <div className="form-group">
                            <label>{t('admin.orders.total')}</label>
                            <input
                              type="text"
                              value={formatCurrency(item.quantity * item.unitPrice)}
                              className="admin-input"
                              disabled
                            />
                          </div>
                        </div>
                        {formData.items.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeItem(index)}
                            className="btn-remove-item"
                            aria-label={t('admin.orders.removeItem')}
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={addItem}
                      className="admin-btn admin-btn-secondary"
                      style={{ marginTop: '1rem' }}
                    >
                      <Plus size={16} />
                      {t('admin.orders.addItem')}
                    </button>
                  </section>

                  {/* Pricing */}
                  <section className="form-section">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                      <h3><DollarSign size={18} /> Prezzi</h3>
                      <button
                        type="button"
                        onClick={getAIQuoteSuggestion}
                        disabled={loadingAI}
                        className="admin-btn admin-btn-secondary"
                        style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                      >
                        <Sparkles size={16} />
                        {loadingAI ? t('admin.orders.aiAnalyzing') : t('admin.orders.aiSuggest')}
                      </button>
                    </div>

                    {aiSuggestion && (
                      <div className="ai-suggestion-box">
                        <h4><Brain size={16} /> {t('admin.orders.aiSuggestions')}</h4>
                        {aiSuggestion.items?.map((item, idx) => (
                          <div key={idx} className="ai-item-suggestion">
                            <strong>{t('admin.orders.aiItem', { n: idx + 1 })}:</strong>
                            <p>{t('admin.orders.aiSuggestedPrice', { price: item.suggestedUnitPrice?.toFixed(2) })}</p>
                            <p className="ai-reasoning">{item.reasoning}</p>
                          </div>
                        ))}
                        {aiSuggestion.recommendations && aiSuggestion.recommendations.length > 0 && (
                          <div className="ai-recommendations">
                            <strong>{t('admin.orders.aiRecommendations')}</strong>
                            <ul>
                              {aiSuggestion.recommendations.map((rec, idx) => (
                                <li key={idx}>{rec}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        <div className="ai-confidence">
                          {t('admin.orders.aiConfidence', { score: aiSuggestion.confidenceScore, time: aiSuggestion.recommendedDeliveryTime })}
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
                          <button
                            type="button"
                            onClick={applyAISuggestion}
                            className="admin-btn admin-btn-primary"
                            style={{ fontSize: '13px', padding: '6px 14px' }}
                          >
                            {t('admin.orders.aiApplyBtn')}
                          </button>
                          <button
                            type="button"
                            onClick={() => setAiSuggestion(null)}
                            className="admin-btn admin-btn-secondary"
                            aria-label={t('admin.orders.aiIgnore')}
                            style={{ fontSize: '13px', padding: '6px 14px' }}
                          >
                            ✕ Ignora
                          </button>
                        </div>
                      </div>
                    )}

                    <div className="pricing-form">
                      <div className="pricing-row">
                        <span>{t('admin.orders.subtotal')}:</span>
                        <strong>{formatCurrency(formData.pricing.subtotal)}</strong>
                      </div>
                      <div className="form-group">
                        <label>{t('admin.orders.labelShipping')}</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={formData.pricing.shipping}
                          onChange={(e) => {
                            const shipping = parseFloat(e.target.value) || 0;
                            setFormData({ ...formData, pricing: { ...formData.pricing, shipping } });
                            calculatePricing(formData.items, shipping, formData.pricing.discount);
                          }}
                          className="admin-input"
                        />
                      </div>
                      <div className="form-group">
                        <label>{t('admin.orders.labelDiscount')}</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={formData.pricing.discount}
                          onChange={(e) => {
                            const discount = parseFloat(e.target.value) || 0;
                            setFormData({ ...formData, pricing: { ...formData.pricing, discount } });
                            calculatePricing(formData.items, formData.pricing.shipping, discount);
                          }}
                          className="admin-input"
                        />
                      </div>
                      <div className="pricing-row">
                        <span>{t('admin.orders.taxLabel22')}:</span>
                        <strong>{formatCurrency(formData.pricing.tax)}</strong>
                      </div>
                      <div className="pricing-row pricing-total">
                        <span>{t('admin.orders.total')}:</span>
                        <strong>{formatCurrency(formData.pricing.total)}</strong>
                      </div>
                    </div>
                  </section>

                  {/* Priority */}
                  <section className="form-section">
                    <h3><AlertCircle size={18} /> {t('admin.orders.priority.label', 'Priority')}</h3>
                    <select
                      value={formData.priority}
                      onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                      className="admin-input"
                    >
                      <option value="low">{t('admin.orders.priority.low')}</option>
                      <option value="normal">{t('admin.orders.priority.normal')}</option>
                      <option value="high">{t('admin.orders.priority.high')}</option>
                      <option value="urgent">{t('admin.orders.priority.urgent')}</option>
                    </select>
                  </section>
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    onClick={() => { setShowCreateModal(false); resetForm(); }}
                    className="admin-btn admin-btn-secondary"
                  >
                    {t('admin.common.cancel')}
                  </button>
                  <button type="submit" className="admin-btn admin-btn-primary">
                    <Save size={18} />
                    {t('admin.orders.createOrder')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Edit Order Modal */}
        {showEditModal && editingOrder && (
          <div className="admin-modal-overlay" onClick={() => setShowEditModal(false)}>
            <div className="admin-modal order-edit-modal" role="dialog" aria-modal="true" aria-labelledby="order-edit-modal-title" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2 id="order-edit-modal-title">{t('admin.orders.editOrderTitle', { number: editingOrder.orderNumber })}</h2>
                <button onClick={() => setShowEditModal(false)} className="modal-close" aria-label={t('admin.common.close')}>
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={handleUpdateOrder}>
                <div className="modal-body">
                  {/* Status */}
                  <section className="form-section">
                    <h3><Settings size={18} /> {t('admin.orders.status.label', 'Status')}</h3>
                    <select
                      value={editingOrder.status}
                      onChange={(e) => setEditingOrder({ ...editingOrder, status: e.target.value })}
                      className="admin-input"
                      autoFocus
                    >
                      <option value="pending">{t('admin.orders.status.pending')}</option>
                      <option value="quoted">{t('admin.orders.status.quoted')}</option>
                      <option value="approved">{t('admin.orders.status.approved')}</option>
                      <option value="in_production">{t('admin.orders.status.in_production')}</option>
                      <option value="quality_check">{t('admin.orders.status.quality_check')}</option>
                      <option value="shipped">{t('admin.orders.status.shipped')}</option>
                      <option value="delivered">{t('admin.orders.status.delivered')}</option>
                      <option value="cancelled">{t('admin.orders.status.cancelled')}</option>
                    </select>
                  </section>

                  {/* Production Info */}
                  {(editingOrder.status === 'in_production' || editingOrder.status === 'quality_check') && (
                    <section className="form-section">
                      <h3><Settings size={18} /> {t('admin.orders.production')}</h3>
                      <div className="form-grid">
                        <div className="form-group">
                          <label>{t('admin.orders.printTime')}</label>
                          <input
                            type="number"
                            step="0.5"
                            value={editingOrder.production?.printTime || ''}
                            onChange={(e) => setEditingOrder({
                              ...editingOrder,
                              production: {
                                ...editingOrder.production,
                                printTime: parseFloat(e.target.value) || 0
                              }
                            })}
                            className="admin-input"
                          />
                        </div>
                        <div className="form-group">
                          <label>{t('admin.orders.machineUsed')}</label>
                          <input
                            type="text"
                            value={editingOrder.production?.machineUsed || ''}
                            onChange={(e) => setEditingOrder({
                              ...editingOrder,
                              production: {
                                ...editingOrder.production,
                                machineUsed: e.target.value
                              }
                            })}
                            className="admin-input"
                          />
                        </div>
                        <div className="form-group">
                          <label>{t('admin.orders.materialUsed')}</label>
                          <input
                            type="number"
                            value={editingOrder.production?.materialUsed || ''}
                            onChange={(e) => setEditingOrder({
                              ...editingOrder,
                              production: {
                                ...editingOrder.production,
                                materialUsed: parseFloat(e.target.value) || 0
                              }
                            })}
                            className="admin-input"
                          />
                        </div>
                      </div>
                    </section>
                  )}

                  {/* Shipping Info */}
                  {(editingOrder.status === 'shipped' || editingOrder.status === 'delivered') && (
                    <section className="form-section">
                      <h3><Truck size={18} /> {t('admin.orders.shipping')}</h3>
                      <div className="form-grid">
                        <div className="form-group">
                          <label>{t('admin.orders.carrier')}</label>
                          <input
                            type="text"
                            value={editingOrder.shipping?.carrier || ''}
                            onChange={(e) => setEditingOrder({
                              ...editingOrder,
                              shipping: {
                                ...editingOrder.shipping,
                                carrier: e.target.value
                              }
                            })}
                            className="admin-input"
                          />
                        </div>
                        <div className="form-group">
                          <label>{t('admin.orders.trackingNumber')}</label>
                          <input
                            type="text"
                            value={editingOrder.shipping?.trackingNumber || ''}
                            onChange={(e) => setEditingOrder({
                              ...editingOrder,
                              shipping: {
                                ...editingOrder.shipping,
                                trackingNumber: e.target.value
                              }
                            })}
                            className="admin-input"
                          />
                        </div>
                      </div>
                    </section>
                  )}

                  {/* Payment Info */}
                  <section className="form-section">
                    <h3><DollarSign size={18} /> Pagamento</h3>
                    <div className="form-grid">
                      <div className="form-group">
                        <label>{t('admin.orders.paymentMethod')}</label>
                        <select
                          value={editingOrder.payment?.method || ''}
                          onChange={(e) => setEditingOrder({
                            ...editingOrder,
                            payment: {
                              ...editingOrder.payment,
                              method: e.target.value
                            }
                          })}
                          className="admin-input"
                        >
                          <option value="">{t('admin.orders.selectOption')}</option>
                          <option value="bank_transfer">{t('admin.orders.payment.bankTransfer')}</option>
                          <option value="card">{t('admin.orders.payment.card')}</option>
                          <option value="cash">{t('admin.orders.payment.cash')}</option>
                          <option value="paypal">PayPal</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label>{t('admin.orders.paymentStatus')}</label>
                        <select
                          value={editingOrder.payment?.status || 'pending'}
                          onChange={(e) => setEditingOrder({
                            ...editingOrder,
                            payment: {
                              ...editingOrder.payment,
                              status: e.target.value
                            }
                          })}
                          className="admin-input"
                        >
                          <option value="pending">{t('admin.orders.payment.status.pending')}</option>
                          <option value="paid">{t('admin.orders.payment.status.paid')}</option>
                          <option value="partial">{t('admin.orders.payment.status.partial')}</option>
                          <option value="refunded">{t('admin.orders.payment.status.refunded')}</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label>{t('admin.orders.paidAmount')}</label>
                        <input
                          type="number"
                          step="0.01"
                          value={editingOrder.payment?.paidAmount || ''}
                          onChange={(e) => setEditingOrder({
                            ...editingOrder,
                            payment: {
                              ...editingOrder.payment,
                              paidAmount: parseFloat(e.target.value) || 0
                            }
                          })}
                          className="admin-input"
                        />
                      </div>
                      <div className="form-group">
                        <label>{t('admin.orders.invoiceNumber')}</label>
                        <input
                          type="text"
                          value={editingOrder.payment?.invoiceNumber || ''}
                          onChange={(e) => setEditingOrder({
                            ...editingOrder,
                            payment: {
                              ...editingOrder.payment,
                              invoiceNumber: e.target.value
                            }
                          })}
                          className="admin-input"
                        />
                      </div>
                    </div>
                  </section>

                  {/* Priority */}
                  <section className="form-section">
                    <h3><AlertCircle size={18} /> {t('admin.orders.priority.label', 'Priority')}</h3>
                    <select
                      value={editingOrder.priority}
                      onChange={(e) => setEditingOrder({ ...editingOrder, priority: e.target.value })}
                      className="admin-input"
                    >
                      <option value="low">{t('admin.orders.priority.low')}</option>
                      <option value="normal">{t('admin.orders.priority.normal')}</option>
                      <option value="high">{t('admin.orders.priority.high')}</option>
                      <option value="urgent">{t('admin.orders.priority.urgent')}</option>
                    </select>
                  </section>
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    onClick={() => handleDeleteOrder(editingOrder._id)}
                    className="admin-btn admin-btn-danger"
                  >
                    <Trash2 size={18} />
                    {t('admin.orders.cancelOrder')}
                  </button>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      type="button"
                      onClick={() => setShowEditModal(false)}
                      className="admin-btn admin-btn-secondary"
                    >
                      {t('admin.common.close')}
                    </button>
                    <button type="submit" className="admin-btn admin-btn-primary">
                      <Save size={18} />
                      {t('admin.orders.saveChanges')}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminOrders;
